import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Pencil, RefreshCw, Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ADJUSTMENT_STATUS_BADGE_VARIANT } from "@/components/payroll/format";
import {
  useDiscardPayrollAdjustment,
  usePayrollAdjustment,
  useRefreshPayrollAdjustmentStatus,
  useTransmitPayrollAdjustment,
} from "@/lib/api/payroll-adjustments.queries";
import { AdjustmentNoteForm } from "@/components/payroll/AdjustmentNoteForm";
import { translatePayrollError } from "@/components/payroll/error-translations";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { useTeamPermission } from "@/lib/team-context";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";
import { useActiveCompany } from "@/lib/company-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { getPayrollAdjustmentArtifact } from "@/lib/api/payroll-adjustments";
import { downloadBlob } from "@/lib/download-blob";
import { safeArtifactFileName } from "@/components/sales-invoices/artifact-download";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/ajustes/$adjustmentId",
)({
  component: AdjustmentDetailPage,
});

function AdjustmentDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <AdjustmentDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function AdjustmentDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, adjustmentId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const adjustmentQ = usePayrollAdjustment(companyId, adjustmentId);
  const transmit = useTransmitPayrollAdjustment(companyId);
  const refresh = useRefreshPayrollAdjustmentStatus(companyId);
  const discard = useDiscardPayrollAdjustment(companyId);
  const canWrite = useTeamPermission("payroll.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const readiness = useCompanyDianReadiness(companyId, "nomina_ajuste");
  const readinessReason = useDianReadinessReason(readiness, t);

  if (adjustmentQ.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (adjustmentQ.isError || !adjustmentQ.data) {
    return (
      <ErrorState
        title={t("payroll.adjustment.detail.error_title")}
        description={t("payroll.adjustment.detail.error_body")}
        onRetry={() => void adjustmentQ.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const a = adjustmentQ.data;
  const canTransmit = a.status === "draft" || a.status === "failed";
  // A draft, or an adjustment DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && a.editable;
  const canDiscard = canWrite && a.status === "draft" && a.documentNumber === 0;

  async function onTransmit() {
    try {
      await transmit.mutateAsync({ adjustmentId });
      toast.success(t("payroll.toast.adjustment_queued", { documentId: a.documentId }));
    } catch (err) {
      showEmitErrorToast(err, translatePayrollError(err, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  async function onRefresh() {
    try {
      await refresh.mutateAsync({ adjustmentId });
      toast.success(t("payroll.toast.refresh_done"));
    } catch (err) {
      toast.error(translatePayrollError(err, t));
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync({ adjustmentId, expectedRevision: a.revision });
      toast.success(t("payroll.toast.adjustment_discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/nomina/ajustes", params: { companyId } });
    } catch (err) {
      toast.error(translatePayrollError(err, t));
      setDiscardOpen(false);
    }
  }

  async function onDownloadPdf() {
    setDownloadingPdf(true);
    try {
      const blob = await getPayrollAdjustmentArtifact(companyId, adjustmentId, "pdf");
      downloadBlob(blob, safeArtifactFileName(a.documentId, "pdf", blob.type));
    } catch {
      toast.error(t("payroll.artifacts.download_error"));
    } finally {
      setDownloadingPdf(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nomina_ajuste" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina/ajustes"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.adjustment.detail.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight font-mono">
            {a.documentId}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(`payroll.adjustment.type.${a.adjustmentType}`)} • {t("payroll.adjustment.original")}{" "}
            {a.originalDocumentId}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {a.status === "transmitted" && ["00", "02"].includes(a.dianStatus ?? "") && a.processId ? (
            <Button variant="outline" disabled={downloadingPdf} onClick={() => void onDownloadPdf()}>
              {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t("payroll.actions.download_pdf")}
            </Button>
          ) : null}
          <Badge
            variant={ADJUSTMENT_STATUS_BADGE_VARIANT[a.status]}
            className="text-[10px] uppercase"
          >
            {t(`payroll.adjustment.status.${a.status}`)}
          </Badge>
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("payroll.actions.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("payroll.actions.discard")}
            </Button>
          ) : null}
          {canTransmit && !editing ? (
            <Button
              onClick={onTransmit}
              disabled={transmit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
            >
              {transmit.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {t("payroll.actions.emit_now")}
            </Button>
          ) : canTransmit ? null : (
            <Button onClick={onRefresh} variant="outline" disabled={refresh.isPending}>
              {refresh.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {t("payroll.actions.refresh_status")}
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <AdjustmentNoteForm
          companyId={companyId}
          initialAdjustment={a}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      {!editing && a.adjustmentType === "replace" && a.replacementTotalPayable !== null ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("payroll.adjustment.detail.replacement_totals")}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <Row
              label={t("payroll.payslip.totals.totalEarnings")}
              value={formatCop(a.replacementTotalEarnings ?? 0)}
            />
            <Row
              label={t("payroll.payslip.totals.totalDeductions")}
              value={formatCop(a.replacementTotalDeductions ?? 0)}
            />
            <Row
              label={t("payroll.payslip.totals.totalPayable")}
              value={formatCop(a.replacementTotalPayable ?? 0)}
              bold
            />
          </CardContent>
        </Card>
      ) : null}

      {a.cune ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("payroll.cune.title")}</CardTitle>
            <CardDescription className="text-xs">
              {t("payroll.cune.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <code className="break-all text-xs font-mono">{a.cune}</code>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("journal.document.title")}</CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="payroll_adjustment_notes"
            documentId={adjustmentId}
          />
        </CardContent>
      </Card>

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("payroll.adjustment.detail.discard_title", { documentId: a.documentId })}
        description={t("payroll.adjustment.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : "text-muted-foreground"}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
